<template>
  <div class="app-container">
    <Header />
    <div class="blog-content-container">
      <div :class="mainContentClass">
        <div class="blog-header">
          <span class="blog-date">{{ date }}</span>
          <h1 class="blog-title">{{ title }}</h1>
          <div class="blog-meta">
            <div class="author-info">
              <img
                :src="authorAvatar || '/blog_assets/logo.png'"
                class="author-avatar"
              />
              <span class="author-name">{{ author || "Skyhost Team" }}</span>
            </div>
          </div>
        </div>
        <div class="geography-blog">
          <div class="blog-hero-image">
            <img :src="heroImage" alt="Blog hero image" class="hero-image" />
          </div>
          <section class="blog-section">
            <h2>Why Flat Maps Fall Short</h2>
            <p>
              When we teach geography with paper maps and static images,
              students miss out on scale, texture, and motion. A contour line on
              a map can't convey the steepness of a volcanic slope or the way
              light plays across a canyon wall. Without that visceral
              connection, it's easy for concepts to remain abstract—and for
              engagement to wane.
            </p>
          </section>
          <section class="blog-section">
            <h2>Immersive Aerial Exploration</h2>
            <ul>
              <li>
                <strong>Volcanic Rims in Full Relief</strong><br />
                A drone ascends the rim of Koko Crater in Hawaii. From the
                classroom screen, students see the gentle inward slope, the
                stark contrast between ash-covered ground and emerald coast, and
                the way shadows deepen as the sun moves.
              </li>
              <li>
                <strong>Coastal Processes Unfolding Live</strong><br />
                The camera glides along Baja California's shore, revealing
                wave-cut platforms, undercut cliffs, and sediment-filled coves.
                Students watch sand build-up in real time and compare sheltered
                bays to exposed headlands—all without leaving their desks.
              </li>
            </ul>
            <p>
              These dynamic aerial views transform landforms from static
              diagrams into living, breathing places.
            </p>
          </section>
          <section class="blog-section">
            <h2>Turning Observation into Inquiry</h2>
            <ul>
              <li>
                <strong>Sketch and Compare</strong><br />
                Ask students to draw the slopes of a glacial valley they've just
                seen and compare their sketches to a topographic map.
              </li>
              <li>
                <strong>Spot Erosion in Action</strong><br />
                Challenge them to identify where water is carving the landscape
                and predict where the next meander or cliff retreat might occur.
              </li>
              <li>
                <strong>Connect Culture and Terrain</strong><br />
                Hover over terraced rice fields in Bali and discuss how
                communities harness slope and water availability to grow
                food—then contrast that with urban sprawl along a floodplain.
              </li>
            </ul>
            <p>
              This back-and-forth between drone and discussion deepens
              understanding and keeps every student's eyes engaged.
            </p>
          </section>
          <section class="blog-section">
            <h2>Seamless Standards Alignment</h2>
            <ul>
              <li>
                <strong>Standards-Mapped Learning Goals</strong><br />
                Each segment corresponds to NGSS, state geography standards, or
                IB requirements—so you can show exactly where a live volcano
                flight meets "Plate Tectonics" or a coastal tour satisfies
                "Human–Environment Interaction."
              </li>
              <li>
                <strong>Ready-Made Discussion Prompts</strong><br />
                From "How does slope affect soil moisture?" to "What happens
                when we build seawalls?", our prompts are designed to spark
                critical thinking and tie observations back to your lesson plan.
              </li>
            </ul>
          </section>
          <section class="blog-section">
            <h2>Building Spatial Thinkers</h2>
            <ul>
              <li>
                <strong>Interactive Controls</strong><br />
                Students can request a closer look—"Pan right 30°," "Descend 20
                m," "Circle the headland"—training them to think in three
                dimensions.
              </li>
              <li>
                <strong>Real-World Navigation</strong><br />
                They learn to interpret map features on the fly, translating
                what they see on screen into compass directions, altitude
                changes, and distance estimates.
              </li>
            </ul>
            <p>
              These skills transfer directly to map reading, GIS applications,
              and everyday problem-solving.
            </p>
          </section>
          <section class="blog-section" style="border-bottom: none">
            <h2>A New Chapter for Geography Class</h2>
            <p>
              Skyhost turns lessons into expeditions. No longer confined to
              worksheets, students embark on live missions: tracing rivers,
              circling volcanoes, and exploring habitats hundreds of miles away.
              Their engagement soars when they realize geography isn't just
              theory—it's a world they can visit in real time.
            </p>
            <p>
              If you're ready to make your next geography unit unforgettable,
              explore our tour catalog and schedule a live drone flight at
              <a
                href="https://skyhost.ai/schools"
                target="_blank"
                class="blog-link"
                >skyhost.ai/schools</a
              >. Bring landscapes, processes, and people together in one
              seamless classroom experience.
            </p>
          </section>
        </div>
        <div class="blog-footer">
          <div class="blog-tags" v-if="tags && tags.length">
            <span class="tag-label">Tags:</span>
            <span v-for="(tag, index) in tags" :key="index" class="blog-tag">
              {{ tag }}
            </span>
          </div>
          <div class="share-section">
            <span class="share-label">Share this article:</span>
            <div class="share-icons">
              <vue-feather
                type="twitter"
                size="18"
                class="share-icon"
                @click="shareOnTwitter"
              ></vue-feather>
              <vue-feather
                type="facebook"
                size="18"
                class="share-icon"
                @click="shareOnFacebook"
              ></vue-feather>
              <vue-feather
                type="linkedin"
                size="18"
                class="share-icon"
                @click="shareOnLinkedIn"
              ></vue-feather>
              <vue-feather
                type="mail"
                size="18"
                class="share-icon"
                @click="shareByEmail"
              ></vue-feather>
            </div>
          </div>
          <div class="related-posts" v-if="relatedBlogs.length">
            <h3 class="related-title">Related articles</h3>
            <div class="related-grid">
              <div
                v-for="relatedBlog in relatedBlogs"
                :key="relatedBlog.id"
                class="related-card"
                @click="navigateToBlog(relatedBlog.id)"
              >
                <img
                  :src="relatedBlog.image"
                  alt="Related blog image"
                  class="related-image"
                />
                <div class="related-content">
                  <h4 class="related-blog-title">{{ relatedBlog.title }}</h4>
                  <span class="related-date">{{ relatedBlog.date }}</span>
                </div>
              </div>
            </div>
          </div>
          <div class="back-to-blogs">
            <button class="blog-btn" @click="navigateToBlogs">
              <vue-feather
                type="arrow-left"
                size="16"
                class="back-icon"
              ></vue-feather>
              Back to all blogs
            </button>
          </div>
        </div>
      </div>
    </div>
    <Footer />
  </div>
</template>

<script>
import { isMobile } from "@/utils/helper";
import { computed } from "vue";
import Header from "@/components/organisms/Header.vue";
import Footer from "@/components/Footer.vue";
import { blogsRegistry } from "@/data/blogRegistry";

export default {
  name: "GeographyBlog",
  components: {
    Header,
    Footer,
  },
  data() {
    const blogId = "geography-drone-lessons";
    const blog = blogsRegistry[blogId];

    return {
      id: blogId,
      title: blog.title,
      date: blog.date,
      author: blog.author,
      authorAvatar: blog.authorAvatar || "/blog_assets/logo.png",
      image: blog.image,
      heroImage: blog.image,
      excerpt: blog.excerpt,
      tags: blog.tags,
      relatedBlogs: [],
    };
  },
  computed: {
    isMobileLayout() {
      return isMobile();
    },
    currentPageUrl() {
      return `${window.location.origin}${this.$route.fullPath}`;
    },
  },
  methods: {
    navigateToBlog(blogId) {
      this.$router.push({
        path: `/blog/${blogId}`,
      });
    },
    shareOnTwitter() {
      const text = `${this.title} - Check out this article by Skyhost`;
      const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(
        text
      )}&url=${encodeURIComponent(this.currentPageUrl)}`;
      window.open(url, "_blank");
    },
    shareOnFacebook() {
      const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
        this.currentPageUrl
      )}`;
      window.open(url, "_blank");
    },
    shareOnLinkedIn() {
      const url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
        this.currentPageUrl
      )}`;
      window.open(url, "_blank");
    },
    shareByEmail() {
      const subject = encodeURIComponent(this.title);
      const body = encodeURIComponent(
        `I thought you might find this article interesting: ${this.title}\n\n${this.currentPageUrl}`
      );
      window.location.href = `mailto:?subject=${subject}&body=${body}`;
    },
    navigateToBlogs() {
      this.$router.push({
        path: "/blogs",
      });
    },
  },
  created() {
    // Get related blogs (excluding current blog)
    this.relatedBlogs = Object.values(blogsRegistry)
      .filter((blog) => blog.id !== this.id)
      .slice(0, 2); // Limit to 2 related blogs
  },
  setup() {
    const mainContentClass = computed(() => {
      return isMobile() ? "mobile-blog-content" : "web-blog-content";
    });
    return {
      mainContentClass,
    };
  },
};
</script>

<style scoped>
.app-container {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background-color: #fff;
  color: #333;
}

.blog-content-container {
  margin-top: 5rem;
}

.web-blog-content {
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem;
}

.mobile-blog-content {
  padding: 1.5rem;
}

.blog-header {
  margin-bottom: 2rem;
}

.blog-date {
  color: #666;
  font-size: 1rem;
}

.blog-title {
  font-size: 2.5rem;
  margin: 1rem 0;
  color: #000;
  line-height: 1.3;
}

.blog-meta {
  display: flex;
  align-items: center;
  margin-bottom: 2rem;
}

.author-info {
  display: flex;
  align-items: center;
}

.author-avatar {
  width: 20px;
  height: 20px;
  /* border-radius: 50%; */
  margin-right: 0.8rem;
  object-fit: contain;
}

.author-name {
  font-weight: 500;
  color: #333;
}

.geography-blog {
  line-height: 1.8;
  color: #333;
  font-family: "Inter", "Helvetica", sans-serif;
}

.blog-hero-image {
  margin-bottom: 2rem;
  border-radius: 8px;
  overflow: hidden;
}

.hero-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  max-height: 500px;
  border-radius: 8px;
}

.blog-intro {
  margin-bottom: 4rem;
}

.intro-text {
  font-size: 1.25rem;
  line-height: 2;
  color: #222;
}

.blog-section {
  margin-bottom: 4rem;
  padding: 2rem 0;
  border-bottom: 1px solid #f0f0f0;
}

.section-content {
  max-width: 100%;
}

h2 {
  font-size: 2rem;
  margin-bottom: 1.5rem;
  color: #000;
  font-weight: 600;
}

h3 {
  font-size: 1.4rem;
  margin-bottom: 1rem;
  color: #000;
  font-weight: 500;
}

p {
  margin-bottom: 1.5rem;
  font-size: 1.05rem;
}

.highlight-box {
  background-color: #f8f9fa;
  border-left: 4px solid #000;
  padding: 1.5rem;
  margin: 2rem 0;
  font-size: 1.1rem;
}

.quote-container {
  text-align: center;
  margin: 3rem 0;
}

blockquote {
  font-size: 1.5rem;
  line-height: 1.6;
  font-style: italic;
  color: #333;
  position: relative;
  max-width: 80%;
  margin: 0 auto;
}

blockquote:before,
blockquote:after {
  content: '"';
  font-size: 3rem;
  color: #ddd;
  position: absolute;
}

blockquote:before {
  left: -2rem;
  top: -1rem;
}

blockquote:after {
  right: -2rem;
  bottom: -2rem;
}

.benefits-cards {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  margin: 2.5rem 0;
}

.benefit-card {
  background-color: #f8f9fa;
  border-radius: 8px;
  padding: 1.5rem;
  height: 100%;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.benefit-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.1);
}

.card-header {
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
}

.benefit-icon {
  font-size: 2rem;
  margin-right: 1rem;
}

.testimonial {
  background-color: #f8f9fa;
  padding: 2rem;
  border-radius: 8px;
  margin-bottom: 2.5rem;
  text-align: center;
}

.testimonial p {
  font-size: 1.3rem;
  font-style: italic;
  margin-bottom: 0.5rem;
}

.author {
  font-weight: 500;
  font-style: normal;
  color: #666;
}

.closing {
  text-align: left;
  max-width: 100%;
  margin: 0 auto;
}

/* .closing h3 {
  font-size: 1.8rem;
  margin-bottom: 1.5rem;
}

.closing p {
  font-size: 1.2rem;
} */

.blog-footer {
  margin-top: 3rem;
  padding-top: 2rem;
  border-top: 1px solid #eee;
}

.blog-tags {
  margin-bottom: 2rem;
}

.tag-label {
  font-weight: 500;
  margin-right: 0.5rem;
}

.blog-tag {
  display: inline-block;
  padding: 0.3rem 0.8rem;
  margin: 0.3rem;
  background-color: #f0f0f0;
  border-radius: 20px;
  font-size: 0.9rem;
  color: #666;
}

.share-section {
  display: flex;
  align-items: center;
  margin-bottom: 3rem;
}

.share-label {
  font-weight: 500;
  margin-right: 1rem;
}

.share-icons {
  display: flex;
  gap: 1rem;
}

.share-icon {
  cursor: pointer;
  color: #555;
}

.share-icon:hover {
  color: #1565c0;
}

.related-posts {
  margin-bottom: 3rem;
}

.related-title {
  font-size: 1.5rem;
  margin-bottom: 1.5rem;
  color: #000;
}

.related-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;
}

.related-card {
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  cursor: pointer;
  transition: transform 0.3s ease;
}

.related-card:hover {
  transform: translateY(-3px);
}

.related-image {
  width: 100%;
  height: 150px;
  object-fit: cover;
}

.related-content {
  padding: 1rem;
}

.related-blog-title {
  font-size: 1.1rem;
  margin-bottom: 0.5rem;
  color: #000;
}

.related-date {
  font-size: 0.8rem;
  color: #666;
}

.back-to-blogs {
  margin-top: 3rem;
  text-align: center;
}

.blog-btn {
  display: inline-flex;
  align-items: center;
  padding: 0.8rem 1.5rem;
  background-color: #f0f0f0;
  border: none;
  border-radius: 30px;
  font-size: 1rem;
  font-weight: 500;
  color: #333;
  cursor: pointer;
  transition: background-color 0.3s ease;
}

.blog-btn:hover {
  background-color: #e0e0e0;
}

.back-icon {
  margin-right: 0.5rem;
}

@media (max-width: 1024px) {
  .benefits-cards {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .benefits-cards {
    grid-template-columns: 1fr;
  }

  blockquote {
    max-width: 100%;
  }

  blockquote:before,
  blockquote:after {
    display: none;
  }

  .closing {
    max-width: 100%;
  }

  .blog-title {
    font-size: 2rem;
  }

  .related-grid {
    grid-template-columns: 1fr;
  }
}

.blog-link {
  color: #1565c0;
  text-decoration: underline;
}
</style>
